import { Button } from '@/components/ui/button'
import { MagicInput as Input } from '@/components/ui/magic-input'
import { Label } from '@/components/ui/label'
import { Combobox } from '@/components/ui/combobox'
import { Info, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { withErrorCode } from '@/lib/errors'
import type { ServiceCardState, AddonState, CatalogData } from '../types'

const COMPLEXITY_LEVELS = ['simple', 'moderate', 'complex'] as const

function isInquiryOffering(pricingType: string): boolean {
  return pricingType === 'starting_at' || pricingType === 'range' || pricingType === 'contact'
}

interface ServiceCardProps {
  card: ServiceCardState
  catalog: CatalogData
  cardIndex: number
  canRemove: boolean
  onServiceChange: (serviceKey: string) => void
  onOfferingChange: (offeringId: string) => void
  onFieldChange: (patch: Partial<ServiceCardState>) => void
  onAddonChange: (addonId: string, patch: Partial<AddonState>) => void
  onRemove: () => void
}

export function ServiceCard({
  card,
  catalog,
  cardIndex,
  canRemove,
  onServiceChange,
  onOfferingChange,
  onFieldChange,
  onAddonChange,
  onRemove,
}: ServiceCardProps) {
  // Offerings belong to a service type either by serviceKey (canonical) or by
  // matching the selected category's title (covers services without serviceKey).
  const selectedCategory = catalog.allCategories.find(
    (c) => c.serviceKey === card.selectedServiceKey
  )
  const availableOfferings = catalog.offerings.filter(
    (o) =>
      o.serviceKey === card.selectedServiceKey ||
      (selectedCategory &&
        o.category.toLowerCase() === selectedCategory.title.toLowerCase())
  )
  const selectedOffering = catalog.offerings.find(
    (o) => o.id === card.selectedOfferingId
  )

  // 72-hour minimum lead time for event bookings.
  // The date input's `min` attribute uses today + 3 days (per-day granularity);
  // a runtime check below combines date + time to enforce the 72h precisely.
  const now = new Date()
  const minDateMs = now.getTime() + 72 * 60 * 60 * 1000
  const minBookingDate = new Date(minDateMs).toISOString().slice(0, 10)
  const leadTimeViolation = (() => {
    if (!card.date || !card.time) return false
    const bookingMs = new Date(`${card.date}T${card.time}`).getTime()
    if (isNaN(bookingMs)) return false
    return bookingMs < minDateMs
  })()

  return (
    <div className="rounded-xl border bg-card p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">
          Service {cardIndex + 1}
          {cardIndex === 0 && (
            <span className="ml-1.5 text-muted-foreground font-normal">(Primary)</span>
          )}
        </h3>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onRemove}
            aria-label="Remove service"
          >
            <Trash2 className="size-4 text-destructive" />
          </Button>
        )}
      </div>

      {/* Service type */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Service Type *</Label>
          <Combobox
            value={card.selectedServiceKey}
            onChange={onServiceChange}
            disabled={catalog.services.length === 0}
            placeholder={catalog.services.length === 0 ? 'No services available' : 'Select a service type…'}
            options={catalog.services.map((service) => ({ label: service.title, value: service.serviceKey }))}
          />
        </div>

        {/* Offering */}
        {card.selectedServiceKey && (
          <div className="space-y-1.5">
            <Label>Service *</Label>
            <Combobox
              value={card.selectedOfferingId}
              onChange={onOfferingChange}
              disabled={availableOfferings.length === 0}
              placeholder="Select a service…"
              options={availableOfferings.map((offering) => ({ label: offering.title, value: offering.id }))}
            />
          </div>
        )}
      </div>

      {/* Inquiry pricing notice */}
      {selectedOffering && isInquiryOffering(selectedOffering.pricingType) && (
        <p className="text-xs text-muted-foreground flex items-start gap-1.5 pt-0.5">
          <Info className="size-3.5 shrink-0 mt-0.5 text-muted-foreground" />
          Pricing for this service requires a custom quote. We'll confirm the details with you after review.
        </p>
      )}

      {/* Event fields */}
      {selectedOffering?.isEvent && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`date-${card.id}`}>Date *</Label>
              <Input
                id={`date-${card.id}`}
                type="date"
                value={card.date}
                min={minBookingDate}
                onChange={(e) => onFieldChange({ date: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`time-${card.id}`}>Time *</Label>
              <Input
                id={`time-${card.id}`}
                type="time"
                value={card.time}
                onChange={(e) => onFieldChange({ time: e.target.value })}
              />
            </div>
          </div>
          {leadTimeViolation && (
            <p className="text-xs text-destructive">
              {withErrorCode(
                'Bookings require at least 72 hours notice. Please pick a later date or time.',
                'request/lead-time',
              )}
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor={`location-${card.id}`}>Location *</Label>
            <Input
              id={`location-${card.id}`}
              placeholder="Venue / Address"
              value={card.location}
              onChange={(e) => onFieldChange({ location: e.target.value })}
            />
          </div>
        </>
      )}

      {/* Hours */}
      {selectedOffering?.priceUnit === 'perHour' && (
        <div className="space-y-1.5 max-w-40">
          <Label htmlFor={`hours-${card.id}`}>Hours *</Label>
          <Input
            id={`hours-${card.id}`}
            type="number"
            min={1}
            value={card.hours}
            onChange={(e) => onFieldChange({ hours: Math.max(1, Number(e.target.value)) })}
          />
        </div>
      )}

      {/* Film length slider */}
      {selectedOffering?.priceUnit === 'perMinute' && (
        <div className="space-y-2">
          <Label>Expected Film Length (minutes) *</Label>
          <div className="space-y-1">
            <input
              type="range"
              min={0.5}
              max={20}
              step={0.5}
              value={card.filmLengthMinutes}
              onChange={(e) => onFieldChange({ filmLengthMinutes: Number(e.target.value) })}
              className="w-full accent-primary"
            />
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0.5}
                step={0.5}
                value={card.filmLengthMinutes}
                onChange={(e) =>
                  onFieldChange({ filmLengthMinutes: Math.max(0.5, Number(e.target.value)) })
                }
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">min</span>
            </div>
          </div>
        </div>
      )}

      {/* Headshots */}
      {selectedOffering?.priceUnit === 'perHeadshot' && (
        <div className="space-y-1.5 max-w-40">
          <Label htmlFor={`headshots-${card.id}`}>Number of Headshots *</Label>
          <Input
            id={`headshots-${card.id}`}
            type="number"
            min={1}
            value={card.headshots}
            onChange={(e) => onFieldChange({ headshots: Math.max(1, Number(e.target.value)) })}
          />
        </div>
      )}

      {/* Complexity */}
      {selectedOffering?.hasComplexity && (
        <div className="space-y-2">
          <Label>Editing Complexity *</Label>
          <div className="flex gap-2">
            {COMPLEXITY_LEVELS.map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => onFieldChange({ complexity: level })}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                  card.complexity === level
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            ))}
          </div>
          {selectedOffering.complexityTexts?.[card.complexity] && (
            <p className="text-xs text-muted-foreground">
              {selectedOffering.complexityTexts[card.complexity]}
            </p>
          )}
        </div>
      )}

      {/* Audio */}
      {selectedOffering?.showAudioOption && (
        <div className="space-y-2">
          <Label>Is audio recording necessary? *</Label>
          <div className="flex items-center gap-6 text-sm">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name={`audio-${card.id}`}
                value="yes"
                checked={card.includeAudioRecording}
                onChange={() => onFieldChange({ includeAudioRecording: true })}
                className="accent-primary"
              />
              Yes
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name={`audio-${card.id}`}
                value="no"
                checked={!card.includeAudioRecording}
                onChange={() => onFieldChange({ includeAudioRecording: false })}
                className="accent-primary"
              />
              No
            </label>
          </div>
        </div>
      )}

      {/* Add-ons */}
      {card.addons.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
          <p className="text-sm font-medium">Add-ons</p>
          <div className="space-y-3">
            {card.addons.map((addon) => (
              <AddonRow
                key={addon.addonId}
                addon={addon}
                cardId={card.id}
                filmLengthMinutes={card.filmLengthMinutes}
                hours={card.hours}
                onAddonChange={onAddonChange}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface AddonRowProps {
  addon: AddonState
  cardId: string
  filmLengthMinutes: number
  hours: number
  onAddonChange: (addonId: string, patch: Partial<AddonState>) => void
}

function AddonRow({ addon, cardId, onAddonChange }: AddonRowProps) {
  const inputId = `addon-${cardId}-${addon.addonId}`

  if (addon.selectionMode === 'boolean') {
    return (
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          type="checkbox"
          checked={addon.boolValue}
          onChange={(e) => onAddonChange(addon.addonId, { boolValue: e.target.checked })}
          className="h-4 w-4 accent-primary"
        />
        <Label htmlFor={inputId} className="cursor-pointer font-normal">
          {addon.label}
          {addon.rate > 0 && (
            <span className="ml-1 text-muted-foreground text-xs">
              +{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(addon.rate)}
              {addon.pricingMode === 'perHour' ? '/hr' : addon.pricingMode === 'perMinute' ? '/min' : ''}
            </span>
          )}
        </Label>
      </div>
    )
  }

  if (addon.selectionMode === 'integer') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={inputId}>
          {addon.label}
          {addon.rate > 0 && (
            <span className="ml-1 text-muted-foreground text-xs font-normal">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(addon.rate)} each
            </span>
          )}
        </Label>
        <Input
          id={inputId}
          type="number"
          min={0}
          max={addon.maxQty}
          value={addon.intValue}
          onChange={(e) =>
            onAddonChange(addon.addonId, { intValue: Math.max(0, Number(e.target.value)) })
          }
          className="w-24"
        />
      </div>
    )
  }

  // string
  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId}>{addon.label}</Label>
      <Input
        id={inputId}
        value={addon.stringValue}
        placeholder="Enter details"
        onChange={(e) => onAddonChange(addon.addonId, { stringValue: e.target.value })}
      />
    </div>
  )
}
